export type Sample = { x: number; y: number; t: number };
export const TAP_MS = 220;
export const MOVE_TOL = 10;
export const FLICK_SPEED = 0.6; // px/ms
export const LONG_MS = 450;
const VEL_WINDOW = 80;

export function travel(samples: Sample[]): number {
  if (samples.length < 2) return 0;
  const a = samples[0], b = samples[samples.length - 1];
  let max = 0;
  for (const s of samples) max = Math.max(max, Math.hypot(s.x - a.x, s.y - a.y));
  return Math.max(max, Math.hypot(b.x - a.x, b.y - a.y));
}

// Velocity over the last VEL_WINDOW ms. At low frame rates moves are coalesced (~33ms on a
// loaded phone, ~90ms in a throttled tab), so the window may hold a single real sample: then
// fall back to the last two samples if they are within FALLBACK_MS of each other. A release
// sample that merely echoes the last move's position (pointerup right after it) is ignored.
const FALLBACK_MS = 150;
const ECHO_MS = 20;
export function releaseVelocity(samples: Sample[]): { vx: number; vy: number; speed: number } {
  let n = samples.length;
  if (n >= 3) {
    const a = samples[n - 2], b = samples[n - 1];
    if (a.x === b.x && a.y === b.y && b.t - a.t <= ECHO_MS) n--;
  }
  if (n < 2) return { vx: 0, vy: 0, speed: 0 };
  const last = samples[n - 1];
  let fi = n - 1;
  for (let i = n - 1; i >= 0 && last.t - samples[i].t <= VEL_WINDOW; i--) fi = i;
  if (fi === n - 1) {
    const prev = samples[n - 2];
    if (last.t - prev.t > FALLBACK_MS) return { vx: 0, vy: 0, speed: 0 };
    fi = n - 2;
  }
  const first = samples[fi];
  const dt = Math.max(1, last.t - first.t);
  const vx = (last.x - first.x) / dt, vy = (last.y - first.y) / dt;
  return { vx, vy, speed: Math.hypot(vx, vy) };
}

export function classifyRelease(samples: Sample[], downAt: number, upAt: number): 'tap' | 'flick' | 'drag-end' {
  const moved = travel(samples);
  if (upAt - downAt < TAP_MS && moved < MOVE_TOL) return 'tap';
  if (releaseVelocity(samples).speed > FLICK_SPEED) return 'flick';
  return moved < MOVE_TOL ? 'tap' : 'drag-end';
}

export function isLongPress(samples: Sample[], downAt: number, now: number): boolean {
  return now - downAt >= LONG_MS && travel(samples) < MOVE_TOL;
}
